/**
 * The signed-in session's secret, held for as long as this window is open (sessionStorage): closing the window signs the person out,
 * and a reload does not. It is never put in localStorage, which outlives the window on a shared computer.
 */
const KEY = 'athlete.session';

function read(): string | null {
  try {
    return window.sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
}

/** Storage can be unavailable (private windows, blocked site data); the session then lasts until the page is reloaded. */
let memory: string | null = read();

/** The service ended a session this window held (as opposed to the person signing out). The sign-in page says so until a new session begins. */
let endedByService = false;

export const sessionToken = {
  get: (): string | null => memory,
  set(token: string) {
    memory = token;
    endedByService = false;
    try {
      window.sessionStorage.setItem(KEY, token);
    } catch {
      /* kept in memory only */
    }
  },
  clear() {
    memory = null;
    try {
      window.sessionStorage.removeItem(KEY);
    } catch {
      /* nothing stored */
    }
  },
};

type EndedHandler = (reason: 'expired') => void;
let onEnded: EndedHandler | null = null;

/** The app registers what happens when the server says the session is over (idle too long, signed out elsewhere, account switched off). */
export function onSessionEnded(handler: EndedHandler | null) {
  onEnded = handler;
}

export function notifySessionEnded() {
  sessionToken.clear();
  endedByService = true;
  onEnded?.('expired');
}

/** True from the moment the service ends this window's session until the person signs in again. */
export const sessionWasEnded = () => endedByService;
